import React, { useState } from 'react';
import { Alert, Box, Button } from '@mui/material';

import { PropertyTextInput } from '@paragon/client-tools';
import { Lumber } from '@paragon/contract-library';
import { NewLumberPrice } from '@paragon/data-connections';
import { hasPricesEditPermission } from '@paragon/login-tools';
import { DistanceField, NumericField } from '@paragon/material';
import { applyLumberPatch, LumberSelects } from '@paragon/material-lists';

import { insertLumberPrice } from '../actions/lumberPriceActions';
import { CallToUpgrade } from './CallToUpgrade';
import { useAppDispatch, useAppSelector } from './hooks';
import { PriceActionBar } from './PriceActionBar';

export function InsertLumberPrice({
  onClose,
  onHeightChange,
}: {
  readonly onClose: () => void;
  readonly onHeightChange: (height: number) => void;
}) {
  const dispatch = useAppDispatch();
  const hasEditPermission = useAppSelector(hasPricesEditPermission);
  const lumberInventory = useAppSelector((state) => state.lumberInventories);

  const [lumber, setLumber] = useState<Lumber | null>(null);
  const [lumberLength, setLumberLength] = useState<number>(120);
  const [pricePerThousandBoardFeet, setPricePerThousandBoardFeet] = useState<number>(0);
  const [notes, setNotes] = useState<string | null>(null);

  // After the lumber inventory has been loaded, set the initially selected lumber to the first one. This should
  // only run once.
  if (!lumber && lumberInventory.length > 0) {
    setLumber(lumberInventory[0]);
  }

  async function onInsertClick() {
    if (!lumber) {
      throw new Error('Error inserting lumber');
    }
    const newLumberPrice: NewLumberPrice = {
      species: lumber.species,
      grade: lumber.grade,
      treatmentType: lumber.treatmentType,
      nominalThicknessInInches: Number(lumber.nominalThickness),
      nominalWidthInInches: Number(lumber.nominalWidth),
      lengthInInches: lumberLength,
      pricePerThousandBoardFeet,
      notes,
    };
    if (await dispatch(insertLumberPrice(newLumberPrice))) onClose();
  }

  if (!lumber) {
    return (
      <PriceActionBar onClose={onClose} onHeightChange={onHeightChange}>
        <Alert severity="warning" sx={{ flex: 1, mr: 4 }}>
          No lumber inventory is available. Reload the page or contact support before adding a lumber price.
        </Alert>
      </PriceActionBar>
    );
  }

  const insertDisabled = lumberLength === 0 || pricePerThousandBoardFeet === 0 || !hasEditPermission;

  return (
    <PriceActionBar component="form" onClose={onClose} onHeightChange={onHeightChange}>
      <Box sx={{ flex: '4 1 420px', display: 'flex' }}>
        <LumberSelects
          strict
          inputSx={{ m: 1, flex: 1 }}
          lumbers={lumberInventory}
          value={lumber}
          disabled={!hasEditPermission}
          onChange={(patch) => setLumber(applyLumberPatch(lumber, patch, lumberInventory))}
        />
      </Box>
      <Box sx={{ display: 'flex', flex: '1 1 520px' }}>
        <DistanceField
          sx={{ m: 1, flex: 1 }}
          fullWidth={false}
          label="Length"
          value={lumberLength}
          min={0}
          max={500 * 12}
          disabled={!hasEditPermission}
          onSubmit={setLumberLength}
        />
        <NumericField
          sx={{ m: 1, flex: 1 }}
          fullWidth={false}
          label="Price 1000 BF"
          value={pricePerThousandBoardFeet}
          min={0}
          disabled={!hasEditPermission}
          onSubmit={setPricePerThousandBoardFeet}
        />
        <PropertyTextInput
          sx={{ m: 1, flex: 1 }}
          value={notes ?? ''}
          property="Notes"
          submit={setNotes}
          disabled={!hasEditPermission}
        />
        <Box sx={{ alignItems: 'center', display: 'flex', flex: 1, m: 1 }}>
          {hasEditPermission ? (
            <Button
              size="small"
              type="button"
              variant="contained"
              onClick={() => void onInsertClick()}
              disabled={insertDisabled}
              sx={{ flex: 1, minHeight: 40 }}
            >
              Insert Lumber Price
            </Button>
          ) : (
            <CallToUpgrade />
          )}
        </Box>
      </Box>
    </PriceActionBar>
  );
}
